// Feature-flagged Supabase client. If REACT_APP_SUPABASE_URL and
// REACT_APP_SUPABASE_ANON_KEY are missing, `supabase` is null and the
// app continues in demo mode. Never put the service-role key here.
import { createClient } from "@supabase/supabase-js";

const url = process.env.REACT_APP_SUPABASE_URL;
const anonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

export const supabase = url && anonKey ? createClient(url, anonKey, {
  auth: { persistSession: true, autoRefreshToken: true, detectSessionInUrl: true },
}) : null;

export const supabaseEnabled = Boolean(supabase);

export async function signUp(email, password) {
  if (!supabase) return { data: null, error: new Error("Supabase not configured") };
  return supabase.auth.signUp({ email, password });
}

export async function signIn(email, password) {
  if (!supabase) return { data: null, error: new Error("Supabase not configured") };
  return supabase.auth.signInWithPassword({ email, password });
}

export async function signOut() {
  if (!supabase) return { error: null };
  return supabase.auth.signOut();
}

export async function getAccessToken() {
  if (!supabase) return null;
  const { data } = await supabase.auth.getSession();
  return data?.session?.access_token || null;
}

export async function uploadAttachment(file) {
  if (!supabase) throw new Error("Supabase not configured");
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error("Please sign in before uploading");
  const safe = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
  const path = `${user.id}/${crypto.randomUUID()}-${safe}`;
  const { error } = await supabase.storage.from("bcsu-attachments").upload(path, file, {
    contentType: file.type || "application/octet-stream",
    upsert: false,
  });
  if (error) throw error;
  return path;
}

export async function signedUrlFor(path, seconds = 600) {
  if (!supabase) throw new Error("Supabase not configured");
  const { data, error } = await supabase.storage.from("bcsu-attachments").createSignedUrl(path, seconds);
  if (error) throw error;
  return data.signedUrl;
}

export function subscribeToNotifications(userId, onInsert) {
  if (!supabase || !userId) return () => {};
  const channel = supabase
    .channel(`notifications:${userId}`)
    .on("postgres_changes", {
      event: "INSERT",
      schema: "public",
      table: "notifications",
      filter: `user_id=eq.${userId}`,
    }, (payload) => onInsert(payload.new))
    .subscribe();
  return () => { supabase.removeChannel(channel); };
}
